/**
 * Default player color — picked automatically when the player creates or joins
 * a game and the color is still free there (otherwise the lowest free color).
 * Saves on tap; reuses the lobby's SlotPicker swatches.
 */

import React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import { PLAYER_COLORS, clampPlayerSlot, PlayerColorChip } from '@warcarp/ui'
import type { UserResponse } from '@warcarp/api-types'
import { SlotPicker } from '../game/SlotPicker'
import { SavedBadge } from './SavedBadge'
import { SettingsDisclosureArrow } from './SettingsDisclosureArrow'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../ui/Toast'
import { apiPatch } from '../../lib/api'

export function DefaultColorPicker() {
  const { user, refreshUser } = useAuth()
  const { showToast } = useToast()
  const [saving, setSaving] = React.useState(false)
  const [expanded, setExpanded] = React.useState(false)
  // Bumped on each successful save to (re)mount the fading "Saved" badge.
  const [savedKey, setSavedKey] = React.useState(0)
  const hideSaved = React.useCallback(() => setSavedKey(0), [])
  const selected = user?.preferredColorSlot ?? null
  const colorName = selected === null ? 'No preference' : PLAYER_COLORS[clampPlayerSlot(selected)].name

  async function save(preferredColorSlot: number | null) {
    if (saving || preferredColorSlot === selected) return
    setSaving(true)
    try {
      await apiPatch<UserResponse>('/api/v1/users/me', { preferredColorSlot })
      await refreshUser()
      setSavedKey(key => key + 1)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <View className="gap-3 px-4 py-4" style={{ opacity: saving ? 0.6 : 1 }}>
      <TouchableOpacity onPress={() => setExpanded(value => !value)} accessibilityRole="button"
        hitSlop={{ top: 6, bottom: 6, left: 0, right: 0 }}
        accessibilityLabel={`Preferred color: ${colorName}`}
        aria-expanded={expanded}
        accessibilityState={{ expanded }}
        style={{ minHeight: 32, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text className="font-stencil text-ink text-sm uppercase flex-1">Preferred Color</Text>
        {selected !== null && <PlayerColorChip slot={clampPlayerSlot(selected)} size="sm" />}
        <Text className="font-typed text-ink-soft text-sm">{colorName}</Text>
        <SettingsDisclosureArrow expanded={expanded} />
      </TouchableOpacity>
      {expanded && <>
      <SlotPicker
        label="Choose your default"
        headingVariant="section"
        players={[]}
        selectedColor={selected}
        onSelect={color => void save(color)}
        noneOption={{ label: 'No preference', onSelect: () => void save(null) }}
        selectedAccessory={savedKey > 0 ? <SavedBadge key={savedKey} onDone={hideSaved} /> : null}
      />
      <Text className="font-typed text-xs text-ink-soft">
        Default when you create or join a game, if no one else has it.
      </Text>
      </>}
    </View>
  )
}
